import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';

export interface CampaignIntakeKey {
  id: string;
  campaign_id: string;
  key_prefix: string;
  label: string | null;
  is_active: boolean;
  last_used_at: string | null;
  request_count: number;
  created_at: string;
}

export interface LeadIntakeEvent {
  id: string;
  received_at: string;
  status: string;
  normalized_phone: string | null;
  source: string | null;
  consent: boolean | null;
  error: string | null;
  raw_payload: Record<string, unknown> | null;
}

/**
 * Manages per-campaign lead-intake webhook API keys + recent inbound leads.
 * Keys are created via the generate-intake-key edge function (which stores only
 * a hash and returns the raw key once); listing/revoking use RLS-protected
 * table access.
 */
export function useCampaignIntakeKeys(campaignId: string | null | undefined) {
  const { toast } = useToast();
  const [keys, setKeys] = useState<CampaignIntakeKey[]>([]);
  const [events, setEvents] = useState<LeadIntakeEvent[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchKeys = useCallback(async () => {
    if (!campaignId) return;
    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('campaign_intake_keys')
        .select('id, campaign_id, key_prefix, label, is_active, last_used_at, request_count, created_at')
        .eq('campaign_id', campaignId)
        .order('created_at', { ascending: false });
      if (error) throw error;
      setKeys((data || []) as CampaignIntakeKey[]);
    } catch (e) {
      console.error('Failed to load intake keys', e);
    } finally {
      setLoading(false);
    }
  }, [campaignId]);

  const fetchEvents = useCallback(async () => {
    if (!campaignId) return;
    try {
      const { data, error } = await supabase
        .from('lead_intake_events')
        .select('id, received_at, status, normalized_phone, source, consent, error, raw_payload')
        .eq('campaign_id', campaignId)
        .order('received_at', { ascending: false })
        .limit(25);
      if (error) throw error;
      setEvents((data || []) as LeadIntakeEvent[]);
    } catch (e) {
      console.error('Failed to load lead intake events', e);
    }
  }, [campaignId]);

  useEffect(() => {
    if (campaignId) {
      fetchKeys();
      fetchEvents();
    } else {
      setKeys([]);
      setEvents([]);
    }
  }, [campaignId, fetchKeys, fetchEvents]);

  /** Generates a new key. Returns the raw key ONCE (caller must show it now). */
  const createKey = useCallback(
    async (label?: string): Promise<string | null> => {
      if (!campaignId) return null;
      try {
        const { data, error } = await supabase.functions.invoke('generate-intake-key', {
          body: { campaignId, label },
        });
        // supabase.functions.invoke sets `error` on non-2xx but the JSON body
        // (which holds our real error message) may still be in data or
        // error.context. Prefer the parsed body when present.
        if (data?.apiKey) {
          await fetchKeys();
          return data.apiKey as string;
        }
        if (error) {
          let serverMsg = error.message;
          try {
            const ctxBody = await (error as { context?: Response })?.context?.json?.();
            if (ctxBody?.error) serverMsg = ctxBody.error;
          } catch {
            /* ignore */
          }
          throw new Error(serverMsg || 'Key generation failed');
        }
        throw new Error(data?.error || 'Key generation failed');
      } catch (e) {
        toast({
          title: 'Could not generate key',
          description: e instanceof Error ? e.message : 'Unknown error',
          variant: 'destructive',
        });
        return null;
      }
    },
    [campaignId, fetchKeys, toast]
  );

  /** Revokes (deactivates) a key. */
  const revokeKey = useCallback(
    async (keyId: string): Promise<boolean> => {
      try {
        const { error } = await supabase
          .from('campaign_intake_keys')
          .update({ is_active: false })
          .eq('id', keyId);
        if (error) throw error;
        await fetchKeys();
        toast({ title: 'Key revoked' });
        return true;
      } catch (e) {
        toast({
          title: 'Could not revoke key',
          description: e instanceof Error ? e.message : 'Unknown error',
          variant: 'destructive',
        });
        return false;
      }
    },
    [fetchKeys, toast]
  );

  /** Revokes ALL active keys for the campaign (used when consent is withdrawn). */
  const revokeAllKeys = useCallback(async (): Promise<boolean> => {
    if (!campaignId) return false;
    try {
      const { error } = await supabase
        .from('campaign_intake_keys')
        .update({ is_active: false })
        .eq('campaign_id', campaignId)
        .eq('is_active', true);
      if (error) throw error;
      await fetchKeys();
      return true;
    } catch (e) {
      console.error('Failed to revoke all keys', e);
      return false;
    }
  }, [campaignId, fetchKeys]);

  return {
    keys,
    events,
    loading,
    createKey,
    revokeKey,
    revokeAllKeys,
    refresh: () => {
      fetchKeys();
      fetchEvents();
    },
  };
}
